import React, { useState, useMemo } from 'react';
import { X, MagnifyingGlass, UserCircle, UsersThree, PaperPlaneRight, Check, CheckSquare, Square } from '@phosphor-icons/react';

/**
 * MultiSelectForwardModal (Point 7 — unified Forward)
 * Forward a message to any mix of customer chats AND groups in one place.
 * Supports text / media / audio (the caller decides what payload to send).
 *
 * Props:
 *   open: boolean
 *   chats: array of { id, customer_name, chat_type }
 *   groups: array of { id, name }            (optional)
 *   onClose: () => void
 *   onForward: ({ chatIds: string[], groupIds: string[] }) => Promise<void>
 *   accentColor: string (tailwind hex bg), default '#0A6F67'
 */
const MultiSelectForwardModal = ({
  open,
  chats = [],
  groups = [],
  onClose,
  onForward,
  accentColor = '#0A6F67',
}) => {
  const [selChats, setSelChats] = useState(new Set());
  const [selGroups, setSelGroups] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const q = search.trim().toLowerCase();
  const filteredChats = useMemo(() => {
    if (!q) return chats;
    return chats.filter(c =>
      (c.customer_name || '').toLowerCase().includes(q) ||
      (c.chat_type || '').toLowerCase().includes(q)
    );
  }, [chats, q]);
  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    return groups.filter(g => (g.name || '').toLowerCase().includes(q));
  }, [groups, q]);

  const totalSelected = selChats.size + selGroups.size;

  const toggleChat = (id) => {
    const next = new Set(selChats);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelChats(next);
  };
  const toggleGroup = (id) => {
    const next = new Set(selGroups);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelGroups(next);
  };

  const allChatsSelected = filteredChats.length > 0 && filteredChats.every(c => selChats.has(c.id));
  const allGroupsSelected = filteredGroups.length > 0 && filteredGroups.every(g => selGroups.has(g.id));

  const toggleAllChats = () => {
    const next = new Set(selChats);
    allChatsSelected ? filteredChats.forEach(c => next.delete(c.id)) : filteredChats.forEach(c => next.add(c.id));
    setSelChats(next);
  };
  const toggleAllGroups = () => {
    const next = new Set(selGroups);
    allGroupsSelected ? filteredGroups.forEach(g => next.delete(g.id)) : filteredGroups.forEach(g => next.add(g.id));
    setSelGroups(next);
  };

  const reset = () => { setSelChats(new Set()); setSelGroups(new Set()); setSearch(''); };

  const handleClose = () => {
    if (sending) return;
    reset();
    onClose && onClose();
  };

  const runForward = async (chatIds, groupIds) => {
    if (chatIds.length + groupIds.length === 0) return;
    setSending(true);
    try {
      await onForward({ chatIds, groupIds });
      reset();
    } finally {
      setSending(false);
    }
  };

  const handleSendSelected = () => runForward(Array.from(selChats), Array.from(selGroups));
  const handleSendAll = () => runForward(chats.map(c => c.id), groups.map(g => g.id));

  if (!open) return null;

  const hasGroups = groups.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-testid="forward-modal-overlay"
    >
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold" data-testid="forward-modal-title">Forward Message</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {totalSelected > 0 ? `${totalSelected} selected` : 'Select chats and/or groups'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            data-testid="forward-modal-close-btn"
            disabled={sending}
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats & groups..."
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ boxShadow: 'none' }}
              data-testid="forward-modal-search"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 pb-3">
          {/* Chats section */}
          <div className="flex items-center justify-between py-2 sticky top-0 bg-white">
            <span className="text-xs font-bold uppercase tracking-wide text-gray-400">Chats</span>
            {filteredChats.length > 0 && (
              <button
                onClick={toggleAllChats}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
                data-testid="forward-select-all-chats"
                disabled={sending}
              >
                {allChatsSelected ? <CheckSquare size={16} weight="fill" style={{ color: accentColor }} /> : <Square size={16} />}
                {allChatsSelected ? 'Deselect' : 'Select all'}
              </button>
            )}
          </div>
          {filteredChats.length === 0 ? (
            <p className="text-gray-400 text-center py-3 text-sm">{chats.length === 0 ? 'No chats available' : 'No matches'}</p>
          ) : (
            <div className="space-y-1.5">
              {filteredChats.map((chat) => {
                const isSelected = selChats.has(chat.id);
                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => toggleChat(chat.id)}
                    className={`w-full px-3 py-2.5 border rounded-lg text-left flex items-center gap-3 transition-colors ${isSelected ? 'border-transparent' : 'border-gray-200 hover:bg-gray-50'}`}
                    style={isSelected ? { backgroundColor: `${accentColor}15`, borderColor: accentColor } : {}}
                    data-testid={`forward-chat-row-${chat.id}`}
                    disabled={sending}
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center" style={isSelected ? { backgroundColor: accentColor, borderColor: accentColor } : { borderColor: '#d1d5db' }}>
                      {isSelected && <Check size={14} weight="bold" color="#fff" />}
                    </div>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e5e7eb' }}>
                      <UserCircle size={28} className="text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate text-sm">{chat.customer_name || 'Unknown Customer'}</div>
                      <div className="text-xs text-gray-500 capitalize">{chat.chat_type} chat</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Groups section */}
          {hasGroups && (
            <>
              <div className="flex items-center justify-between py-2 mt-3 sticky top-0 bg-white">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-400">Groups</span>
                {filteredGroups.length > 0 && (
                  <button
                    onClick={toggleAllGroups}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
                    data-testid="forward-select-all-groups"
                    disabled={sending}
                  >
                    {allGroupsSelected ? <CheckSquare size={16} weight="fill" style={{ color: accentColor }} /> : <Square size={16} />}
                    {allGroupsSelected ? 'Deselect' : 'Select all'}
                  </button>
                )}
              </div>
              {filteredGroups.length === 0 ? (
                <p className="text-gray-400 text-center py-3 text-sm">{groups.length === 0 ? 'No groups available' : 'No matches'}</p>
              ) : (
                <div className="space-y-1.5">
                  {filteredGroups.map((group) => {
                    const isSelected = selGroups.has(group.id);
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className={`w-full px-3 py-2.5 border rounded-lg text-left flex items-center gap-3 transition-colors ${isSelected ? 'border-transparent' : 'border-gray-200 hover:bg-gray-50'}`}
                        style={isSelected ? { backgroundColor: `${accentColor}15`, borderColor: accentColor } : {}}
                        data-testid={`forward-group-row-${group.id}`}
                        disabled={sending}
                      >
                        <div className="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center" style={isSelected ? { backgroundColor: accentColor, borderColor: accentColor } : { borderColor: '#d1d5db' }}>
                          {isSelected && <Check size={14} weight="bold" color="#fff" />}
                        </div>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-green-100">
                          <UsersThree size={20} className="text-green-700" weight="bold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{group.name || 'Group'}</div>
                          <div className="text-xs text-gray-500">Group</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t px-4 py-3 flex gap-2">
          <button
            onClick={handleSendAll}
            disabled={chats.length + groups.length === 0 || sending}
            className="flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: accentColor, color: accentColor }}
            data-testid="forward-send-to-all-btn"
          >
            Send to All ({chats.length + groups.length})
          </button>
          <button
            onClick={handleSendSelected}
            disabled={totalSelected === 0 || sending}
            className="flex-1 px-3 py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: accentColor }}
            data-testid="forward-send-selected-btn"
          >
            {sending ? 'Sending...' : (<><PaperPlaneRight size={16} weight="fill" /> Send ({totalSelected})</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiSelectForwardModal;
